import { InfoCard } from '#app/components/ui/info-card.tsx'
import { type Route } from './+types/nationals.competition.ts'

type QuickLinkSection = {
	id: string
	label: string
	cards: Array<{ id: string; label: string }>
}

const QUICK_LINK_SECTIONS: QuickLinkSection[] = [
	{ id: 'fees-time-limits', label: 'Fees & Time Limits', cards: [] },
	{ id: 'age-divisions', label: 'Age Divisions', cards: [] },
	{
		id: 'performance-style',
		label: 'Performance Style',
		cards: [
			{ id: 'style-ballet', label: 'Ballet' },
			{ id: 'style-tap', label: 'Tap' },
			{ id: 'style-jazz', label: 'Jazz' },
			{ id: 'style-lyrical', label: 'Lyrical' },
			{ id: 'style-contemporary', label: 'Contemporary' },
			{ id: 'style-musical-theatre', label: 'Musical Theatre' },
			{ id: 'style-hip-hop', label: 'Hip Hop' },
			{ id: 'style-open', label: 'Open' },
			{ id: 'style-adjudication-only', label: 'Adjudication Only' },
		],
	},
	{ id: 'judging-criteria', label: 'Judging Criteria', cards: [] },
	{
		id: 'general-rules',
		label: 'General Rules & Regulations',
		cards: [
			{ id: 'rule-live-stream', label: 'Live Stream & Media' },
			{ id: 'rule-free-admission', label: 'Free Admission' },
			{ id: 'rule-vip-seating', label: 'VIP Seating' },
			{ id: 'rule-convention-requirements', label: 'Convention Requirements' },
			{ id: 'rule-studio-representation', label: 'Studio Representation' },
			{
				id: 'rule-teacher-professional',
				label: 'Teacher & Professional Dancers',
			},
			{ id: 'rule-refund-policy', label: 'Refund Policy' },
			{ id: 'rule-media-rights', label: 'Media Rights' },
			{ id: 'rule-stage-entrances', label: 'Stage Entrances & Exits' },
			{ id: 'rule-judge-conflict', label: 'Judge Conflict of Interest' },
		],
	},
	{
		id: 'nationals-rules',
		label: 'Nationals Rules & Regulations',
		cards: [
			{ id: 'nat-routine-requirements', label: 'Routine Requirements' },
			{ id: 'nat-category-changes', label: 'Category Changes' },
			{ id: 'nat-multiple-showrooms', label: 'Multiple Showrooms' },
			{ id: 'nat-check-in', label: 'Check-In' },
			{ id: 'nat-atmosphere', label: 'DanceMakers Atmosphere' },
			{ id: 'nat-entry-limitations', label: 'Entry Limitations' },
		],
	},
	{
		id: 'competition-details',
		label: 'Competition Details',
		cards: [
			{ id: 'detail-music', label: 'Competition Music' },
			{ id: 'detail-judging-packet', label: 'Competition Judging Packet' },
			{ id: 'detail-photo-video', label: 'Photo & Video' },
			{ id: 'detail-props', label: 'Props' },
		],
	},
	{
		id: 'pre-qualifying',
		label: 'Pre-Qualifying Video Submissions',
		cards: [
			{
				id: 'pre-qualifying-digital',
				label: 'Nationals Digital Pre-Qualifying',
			},
		],
	},
	{ id: 'questions', label: 'Questions', cards: [] },
]

function QuickLinksPanel() {
	return (
		<div className="border border-muted-foreground/30 bg-muted/30 p-4 sm:p-6">
			<div className="mb-4 flex items-center gap-3 sm:mb-5">
				<div className="flex h-8 w-8 items-center justify-center text-primary">
					<svg
						className="h-5 w-5"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						strokeWidth={2.5}
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
						/>
					</svg>
				</div>
				<h3 className="font-display text-base font-bold tracking-wide sm:text-lg">
					QUICK LINKS
				</h3>
			</div>

			<div className="space-y-3">
				{QUICK_LINK_SECTIONS.map((section, sectionIndex) => (
					<div
						key={section.id}
						className="border border-border/50 bg-background/95 p-3"
					>
						<div className="mb-2 flex items-center gap-2">
							<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-foreground">
								{sectionIndex + 1}
							</span>
							<a
								href={`#${section.id}`}
								className="inline-flex flex-1 items-center border border-border/60 bg-muted/40 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-foreground transition-colors hover:border-primary/50 hover:text-primary"
							>
								<span>{section.label}</span>
							</a>
						</div>

						{section.cards.length > 0 ? (
							<div className="ml-2 space-y-1.5 border-l border-border/50 pl-3">
								{section.cards.map((card) => (
									<a
										key={card.id}
										href={`#${card.id}`}
										className="group flex items-start gap-2 rounded-sm border border-transparent px-2 py-1.5 text-xs leading-snug text-foreground/85 transition-all hover:border-border/60 hover:bg-muted/40 hover:text-foreground"
									>
										<span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary/60 transition-colors group-hover:bg-primary" />
										<span className="flex-1">{card.label}</span>
									</a>
								))}
							</div>
						) : (
							<p className="ml-2 text-xs text-muted-foreground">
								General section details.
							</p>
						)}
					</div>
				))}
			</div>
		</div>
	)
}

export const meta: Route.MetaFunction = () => [
	{ title: 'Nationals Competition Guidelines | DanceMakers' },
	{
		name: 'description',
		content:
			'Complete Nationals competition guidelines including fees, age divisions, performance styles, judging criteria, and rules for DanceMakers Nationals in Myrtle Beach, SC.',
	},
]

export default function NationalsCompetitionRoute() {
	return (
		<div className="min-h-screen pt-12 md:pt-16">
			{/* Hero Section */}
			<section className="container px-6 pb-8 pt-12 md:px-16 md:pb-10 md:pt-16">
				<div className="max-w-3xl">
					<p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
						2026-27 Season
					</p>
					<h1 className="mb-6 font-display text-3xl font-bold uppercase leading-tight md:text-4xl lg:text-5xl">
						Nationals Competition
					</h1>
					<p className="text-sm leading-relaxed text-muted-foreground">
						Everything you need to know about competing at DanceMakers
						Nationals. Review fees, time limits, age divisions, styles, and
						competition rules.
					</p>
				</div>
			</section>

			{/* Desktop-only floating quick links */}
			<div className="lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start lg:gap-8 lg:px-6 xl:px-12">
				<div className="min-w-0">
					{/* Competition Fees & Time Limits */}
					<section
						id="fees-time-limits"
						className="scroll-mt-36 bg-muted/30 py-8 md:py-12"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Fees & Time Limits
							</h2>

							<div className="-mx-6 overflow-x-auto px-6 md:mx-0 md:px-0">
								<table className="w-full border border-muted-foreground/30 text-xs sm:text-sm">
									<thead>
										<tr className="bg-muted/30">
											<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Category
											</th>
											<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Fee
											</th>
											<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Time
											</th>
											<th className="border-b border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Dancers
											</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Solo
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-4">
												$215
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												3:00
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												1
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Duo/Trio
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-4">
												$115
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												3:00
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												2-3
											</td>
										</tr>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Group
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-4">
												$95
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												3:00
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												4-9
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Line
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-4">
												$95
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												4:00
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												10-15
											</td>
										</tr>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Ext. Line
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-4">
												$95
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												5:00
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												16-24
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Production
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-4">
												$95
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												6:00
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												25+
											</td>
										</tr>
										<tr>
											<td className="border-r border-muted-foreground/30 p-2 sm:p-4">
												Media Fee
											</td>
											<td colSpan={3} className="p-2 font-medium sm:p-4">
												$45 / competing dancer (not per routine)
											</td>
											{/* <td className="border-r border-muted-foreground/30 p-2 sm:p-4">
											</td>
											<td className="p-2 sm:p-4">per Dancer</td> */}
										</tr>
									</tbody>
								</table>
							</div>

							<div className="mt-4 max-w-4xl space-y-3 text-xs leading-relaxed text-muted-foreground sm:mt-6 sm:space-y-4 sm:text-sm">
								<p>
									<span className="font-medium text-foreground">
										All fees are per dancer per routine.
									</span>
								</p>
								<p>
									You may purchase up to{' '}
									<span className="font-medium text-foreground">
										3 additional minutes of time
									</span>{' '}
									for any Group, Line, Extended Line, or Production for{' '}
									<span className="font-medium text-foreground">
										$5 per dancer, per additional minute
									</span>
									. Please contact home office for this adjustment.{' '}
									<span className="font-medium text-foreground">
										A 5 point deduction will be taken if the time limit is
										exceeded.
									</span>
								</p>
								<p>
									<span className="mr-1.5 font-bold text-primary underline">
										NEW Media Fee:
									</span>
									A $45 media fee per competing dancer (not per routine) is
									required if participating in competition and is automatically
									applied to the studio's registration total. Each competing
									dancer will receive all media captured of all competing routines
									within 3-5 business days of the conclusion of their event. Media
									will be distributed through the registered studio, and no action
									is required to receive this media.
								</p>
								<p>
									Included with the media fee, this package also includes all
									photos posted to our social media platforms as an added bonus,
									including convention, competition, awards, and other event
									photos.
								</p>
							</div>
						</div>
					</section>

					{/* Age Divisions */}
					<section id="age-divisions" className="scroll-mt-36 py-10 md:py-16">
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Age Divisions
							</h2>

							<div className="-mx-6 max-w-2xl overflow-x-auto px-6 md:mx-0 md:px-0">
								<table className="w-full border border-muted-foreground/30 text-xs sm:text-sm">
									<thead>
										<tr className="bg-muted/30">
											<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Division
											</th>
											<th className="border-b border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Age
											</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Senior
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												15 & Up
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Teen
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												13-14
											</td>
										</tr>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Pre-Teen
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												10-12
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Junior
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												7-9
											</td>
										</tr>
										<tr>
											<td className="border-r border-muted-foreground/30 p-2 sm:p-4">
												Camp Dance
											</td>
											<td className="p-2 font-medium sm:p-4">3-6</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div className="mt-4 max-w-4xl space-y-3 text-xs leading-relaxed text-muted-foreground sm:mt-6 sm:text-sm">
								<p>
									<span className="font-medium text-foreground">
										The age of the student is their age as of Jan 1, 2027.
									</span>
								</p>
								<p>
									Age of group is determined by adding students age together and
									dividing by the number of dancers in the routine. Disregard
									decimal points.
								</p>
							</div>
						</div>
					</section>

					{/* Performance Style */}
					<section
						id="performance-style"
						className="scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Performance Style
							</h2>

							<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
								<div id="style-ballet" className="scroll-mt-36">
									<InfoCard title="Ballet">
										<p>
											Classical Ballet steps using slippers or pointe shoes
											including Classical or Pointe
										</p>
									</InfoCard>
								</div>

								<div id="style-tap" className="scroll-mt-36">
									<InfoCard title="Tap">
										<p>Tap technique</p>
									</InfoCard>
								</div>

								<div id="style-jazz" className="scroll-mt-36">
									<InfoCard title="Jazz">
										<p>Any Jazz technique including up tempo music and movement</p>
									</InfoCard>
								</div>

								<div id="style-lyrical" className="scroll-mt-36">
									<InfoCard title="Lyrical">
										<p>
											Lyrical technique with emphasis on the lyrics or mood of the
											music
										</p>
									</InfoCard>
								</div>

								<div id="style-contemporary" className="scroll-mt-36">
									<InfoCard title="Contemporary">
										<p>
											Mix of styles with emphasis on Modern Technique, Ballet or
											Contemporary movement
										</p>
									</InfoCard>
								</div>

								<div id="style-musical-theatre" className="scroll-mt-36">
									<InfoCard title="Musical Theatre">
										<p>
											Any performance utilizing the style or interpretation of
											Broadway, Musical Theatre or Hollywood choreography
										</p>
									</InfoCard>
								</div>

								<div id="style-hip-hop" className="scroll-mt-36">
									<InfoCard title="Hip Hop">
										<p>Hip Hop or street moves</p>
									</InfoCard>
								</div>

								<div id="style-open" className="scroll-mt-36">
									<InfoCard title="Open">
										<p className="mb-3">
											Combination of any of the above styles – A routine that
											includes 4 or more tricks (a trick is considered any
											movement when dancers feet go over their head).
										</p>
										<p>
											Or Acro-Gymnastics – Novelty – Clogging – Ethnic – Pom-Poms
											– Ballroom.
										</p>
									</InfoCard>
								</div>

								<div id="style-adjudication-only" className="scroll-mt-36">
									<InfoCard title="Adjudication Only">
										<p>
											Entries that wish to perform for adjudication and judges
											critiques only. They will not be eligible for Style High
											Scores, DanceAgains!, and any overall awards. These routines
											are eligible to qualify for Nationals.
										</p>
									</InfoCard>
								</div>
							</div>

							<p className="mt-4 max-w-4xl text-xs leading-relaxed text-muted-foreground sm:mt-6 sm:text-sm">
								<span className="font-medium text-foreground">
									Judges may change a style if they feel it is incorrectly
									placed.
								</span>{' '}
								There is no penalty for this change.
							</p>
						</div>
					</section>

					{/* Judging Criteria */}
					<section id="judging-criteria" className="scroll-mt-36 py-10 md:py-16">
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Judging Criteria
							</h2>

							<div className="-mx-6 max-w-2xl overflow-x-auto px-6 md:mx-0 md:px-0">
								<table className="w-full border border-muted-foreground/30 text-xs sm:text-sm">
									<thead>
										<tr className="bg-muted/30">
											<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Criteria
											</th>
											<th className="border-b border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Points
											</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Technique
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												40 pts
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Choreography
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												20 pts
											</td>
										</tr>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Stage Presence
											</td>
											<td className="border-b border-muted-foreground/30 p-2 font-medium sm:p-4">
												20 pts
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-r border-muted-foreground/30 p-2 sm:p-4">
												Overall Impact
											</td>
											<td className="p-2 font-medium sm:p-4">20 pts</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</section>

					{/* General Rules & Regulations */}
					<section
						id="general-rules"
						className="scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
								General Rules & Regulations
							</h2>

							<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2">
								<div id="rule-live-stream" className="scroll-mt-36">
									<InfoCard title="Live Stream & Media">
										<p className="mb-3">
											We are excited to offer a{' '}
											<span className="font-medium text-foreground">
												live stream for all competition and award ceremonies
											</span>
											. Please be aware this is a free service offered and
											ultimately dependent on the internet strength at each venue.
										</p>
										<p>
											Competition photos and videos are now distributed to all
											dancers competing. See more above in the Fees and Time
											Limits charts about the new media fee!
										</p>
									</InfoCard>
								</div>

								<div id="rule-free-admission" className="scroll-mt-36">
									<InfoCard title="Free Admission">
										<p className="mb-2 font-medium text-foreground">
											Admission to DanceMakers competition is absolutely free!
										</p>
										<p>
											All registered attendees, as well as family and friends,
											are welcome to watch at no cost. Come and support your
											dancers!
										</p>
									</InfoCard>
								</div>

								<div id="rule-vip-seating" className="scroll-mt-36">
									<InfoCard title="VIP Seating">
										<p>
											VIP Seating will be available for teachers at competition.
										</p>
									</InfoCard>
								</div>

								<div id="rule-convention-requirements" className="scroll-mt-36">
									<InfoCard title="Convention Requirements">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												All competing dancers must be registered for the full week
												of convention
											</span>{' '}
											in order to participate in competition.
										</p>
										<p>
											Convention wristbands must be worn while competing. Please
											do not remove convention wristbands once they are secured on
											the wrist.
										</p>
									</InfoCard>
								</div>

								<div id="rule-studio-representation" className="scroll-mt-36">
									<InfoCard title="Studio Representation">
										<p className="mb-3">
											All competing dancers must have studio representation in
											order to participate in competition.{' '}
											<span className="font-medium text-foreground">
												DanceMakers does not accept independent competition
												registrations.
											</span>
										</p>
										<p>
											Dancers are only able to be registered and compete under one
											studio.
										</p>
									</InfoCard>
								</div>

								<div id="rule-teacher-professional" className="scroll-mt-36">
									<InfoCard title="Teacher & Professional Dancers">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												Teachers and/or professional dancers are not permitted to
												compete.
											</span>
										</p>
										<p>
											Teachers will be responsible for producing birth
											certificates to verify age, should challenges or questions
											arise.
										</p>
									</InfoCard>
								</div>

								<div id="rule-refund-policy" className="scroll-mt-36">
									<InfoCard title="Refund Policy">
										<p>
											<span className="font-medium text-foreground">
												There are no refunds or credits for competition fees.
											</span>
										</p>
									</InfoCard>
								</div>

								<div id="rule-media-rights" className="scroll-mt-36">
									<InfoCard title="Media Rights">
										<p>
											Choreographers and all attendees automatically give
											permission to DanceMakers to use all photos and videos for
											advertising and publicity.
										</p>
									</InfoCard>
								</div>

								<div id="rule-stage-entrances" className="scroll-mt-36">
									<InfoCard title="Stage Entrances & Exits">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												All entrances and exits must be done from stage left or
												right.
											</span>{' '}
											No entrances or exits from front or back of stage.
										</p>
										<p>
											Talking, lip syncing and singing are acceptable.{' '}
											<span className="font-medium text-foreground">
												Microphones are prohibited.
											</span>
										</p>
									</InfoCard>
								</div>

								<div id="rule-judge-conflict" className="scroll-mt-36">
									<InfoCard title="Judge Conflict of Interest">
										<p>
											If a routine performing was choreographed by one of the
											judges, that judges score will be an average of the other
											two judges.
										</p>
									</InfoCard>
								</div>
							</div>
						</div>
					</section>

					{/* Nationals Rules & Regulations */}
					<section
						id="nationals-rules"
						className="scroll-mt-36 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
								Nationals Rules & Regulations
							</h2>

							<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2">
								<div id="nat-routine-requirements" className="scroll-mt-36">
									<InfoCard title="Routine Requirements">
										<p className="mb-3">
											<span className="font-medium text-foreground">
												50% of the dancers, choreography and music
											</span>{' '}
											in the Nationals routine must be from the Regional routine.
										</p>
										<p>
											Competition routines{' '}
											<span className="font-medium text-foreground">
												may not change subject
											</span>{' '}
											from what it qualified in at Regionals.
										</p>
									</InfoCard>
								</div>

								<div id="nat-category-changes" className="scroll-mt-36">
									<InfoCard title="Category Changes">
										<p className="mb-3">
											Competition routines may only move{' '}
											<span className="font-medium text-foreground">
												up or down ONE category
											</span>
											.
										</p>
										<p className="mb-3">
											Example: a line can move up one category to an extended line
											or down one category to a group. The line could not move
											down to a duo/trio or up to a production.
										</p>
										<p>
											<span className="font-medium text-foreground">
												Exceptions:
											</span>{' '}
											A duo/trio cannot move down to a solo, and a solo cannot
											move up to a duo/trio.
										</p>
									</InfoCard>
								</div>

								<div id="nat-multiple-showrooms" className="scroll-mt-36">
									<InfoCard title="Multiple Showrooms">
										<p className="mb-3">
											DanceMakers will run{' '}
											<span className="font-medium text-foreground">
												two showrooms simultaneously
											</span>{' '}
											if needed based off registrations.
										</p>
										<p className="mb-3">
											All routines will be placed specifically to avoid studio
											performance time conflicts.
										</p>
										<p>
											Unfortunately you will not be allowed to perform at any
											other time than your assigned performance time to avoid
											studio performance time conflicts.
										</p>
									</InfoCard>
								</div>

								<div id="nat-check-in" className="scroll-mt-36">
									<InfoCard title="Check-In">
										<p>
											Dancers will check in with the Girl's Dressing Room Manager{' '}
											<span className="font-medium text-foreground">
												30 minutes prior to their performance time
											</span>
											.
										</p>
									</InfoCard>
								</div>

								<div id="nat-atmosphere" className="scroll-mt-36">
									<InfoCard title="DanceMakers Atmosphere">
										<p className="mb-3">
											In an effort to maintain the values of the DanceMakers
											atmosphere and a family competition,{' '}
											<span className="font-medium text-foreground">
												DanceMakers expects that all routines be age, music and
												costume appropriate
											</span>
											.
										</p>
										<p className="mb-3">
											All of these issues are subjective. We are aware that our
											guidelines may be too strict for some and not strict enough
											for others. There is a fine line that we take very
											seriously.
										</p>
										<p>
											<span className="font-medium text-foreground">
												Routines will receive an automatic third place
											</span>{' '}
											if our judges feel a routine is inappropriate.
										</p>
									</InfoCard>
								</div>

								<div id="nat-entry-limitations" className="scroll-mt-36">
									<InfoCard title="Entry Limitations">
										<p className="font-medium text-foreground">
											<span className="font-medium text-primary">
												New this year:
											</span>{' '}
											There are no competition limitations for overall routines
											for our Nationals event. The only limitation is that
											dancers are limited to one solo at Nationals. If dancers
											qualified more than one solo regionally, they may only
											register one for Nationals.
										</p>
									</InfoCard>
								</div>
							</div>
						</div>
					</section>

					{/* Competition Details */}
					<section
						id="competition-details"
						className="scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
								Competition Details
							</h2>

							<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2">
								<div id="detail-music" className="scroll-mt-36">
									<InfoCard title="Competition Music">
										<p className="mb-3">
											We ask that all competition music is uploaded by{' '}
											<span className="font-medium text-foreground">
												the Wednesday before your event
											</span>
											.
										</p>
										<p className="mb-3">
											Please log into your DanceMakers{' '}
											<a
												className="font-bold text-primary hover:underline"
												href="https://dancemakersinc.io"
												target="_blank"
												rel="noopener noreferrer"
											>
												registration account
											</a>{' '}
											to upload music by clicking "Music Uploads" feature for
											your event.
										</p>
										<p className="mb-3">
											<span className="font-medium text-foreground">
												MP3 or MP4 files
											</span>{' '}
											are accepted. Once uploaded you can preview all music!
										</p>
										<p>
											Please have a backup of your music with you on either a
											smartphone or USB at the event. If you need assistance,
											please contact our admin team prior to your event.
										</p>
									</InfoCard>
								</div>

								<div id="detail-judging-packet" className="scroll-mt-36">
									<InfoCard title="Competition Judging Packet">
										<p className="mb-3">
											In efforts to cut down on our environmental footprint, we
											will be uploading all scores to the studio's online
											registration portal.
										</p>
										<p className="mb-3">
											At Nationals,{' '}
											<span className="font-medium text-foreground">
												all routines will receive an adjudication score only
											</span>
											. Routines will not be receiving a judge's critique.
										</p>
										<p>
											All critiques of routines were given at our Regional events
											to help the dancers improve to compete at Nationals.
										</p>
									</InfoCard>
								</div>

								<div id="detail-photo-video" className="scroll-mt-36">
									<InfoCard title="Photo & Video">
										<p className="mb-3 font-medium text-foreground">
											Photography and videography during competition are strictly
											prohibited.
										</p>
										<p className="mb-3">
											Advanced technology of video on cell phones, cameras, etc.,
											has forced us to prohibit all camera use during competition.{' '}
											<span className="font-medium text-foreground">
												This includes ALL still photography, with or without
												flash.
											</span>
										</p>
										<p className="mb-3">
											This is to protect the rights of the choreographer as well
											as your children's safety, and to make it possible for
											DanceMakers to offer a professional photography and video
											service.
										</p>
										<p className="mb-3">
											We have a team of professionals capturing competition and
											will have all photo and video available for instant preview
											in the lobby!
										</p>
										<p>
											<span className="font-medium text-foreground">
												Any violations can result in a routine and/or studio
												disqualification.
											</span>
										</p>
									</InfoCard>
								</div>

								<div id="detail-props" className="scroll-mt-36">
									<InfoCard title="Props">
										<p className="mb-3">
											DanceMakers welcomes general props to our competition!
											However, the use of props is the sole responsibility of the
											studio.
										</p>
										<p className="mb-3">
											Studios have a total of{' '}
											<span className="font-medium text-foreground">
												two minutes to place and remove their props from the
												stage
											</span>
											. Violation of this rule will result in a 5 point
											reduction.
										</p>
										<p className="mb-3">
											It is also the studio's responsibility to ensure the stage
											is left clean and clear following their performance.
											Teachers, dancers and parents are more than welcome to help
											with props as long as the judges cannot see any studio
											affiliation.
										</p>
										<p className="mb-3">
											Please refrain from using{' '}
											<span className="font-medium text-foreground">
												fire or helium balloons
											</span>{' '}
											as well as props being thrown into the judges area and/or
											audience.
										</p>
										<p className="mb-3">
											DanceMakers cannot guarantee props over{' '}
											<span className="font-medium text-foreground">10'h</span>{' '}
											will fit on the stage.
										</p>
										<p className="text-xs">
											If your prop cannot fit through a single door, please reach
											out to jonathan@dancemakersinc.com to coordinate a time to
											get your props into the showroom. We will only have
											designated times available for this.
										</p>
									</InfoCard>
								</div>
							</div>
						</div>
					</section>

					{/* Pre-Qualifying Video Submissions */}
					<section id="pre-qualifying" className="scroll-mt-36 py-10 md:py-16">
						<div className="container px-6 md:px-16">
							<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
								Pre-Qualifying Video Submissions
							</h2>

							<div id="pre-qualifying-digital" className="max-w-4xl scroll-mt-36">
								<InfoCard title="Nationals Digital Pre-Qualifying">
									<p className="mb-3">
										DanceMakers is offering{' '}
										<span className="font-medium text-foreground">
											Nationals Digital Pre-Qualifying Submissions
										</span>{' '}
										again this year!
									</p>
									<p className="mb-3">
										Studios, register your solo or group routines to pre-qualify
										for our Nationals event in Myrtle Beach, SC. Competition fees
										are the same as regionals.
									</p>
									<p className="mb-3">
										Each routine submitted will be critiqued and scored by our
										judges.{' '}
										<span className="font-medium text-foreground">
											All routines that score 80 or above will qualify for
											Nationals!
										</span>
									</p>
									<div className="mt-4 space-y-2 border-t border-muted-foreground/30 pt-4">
										<p>
											<span className="font-medium text-foreground">
												Submissions Open:
											</span>{' '}
											March 4th
										</p>
										<p>
											<span className="font-medium text-foreground">
												Payment & Video Due:
											</span>{' '}
											April 9th
										</p>
										<p>
											<span className="font-medium text-foreground">
												Results Delivered:
											</span>{' '}
											May 5th
										</p>
									</div>
									<p className="mt-4">
										All qualified routines will be available in your account to
										register for Nationals competition in Myrtle Beach, SC at that
										time.
									</p>
								</InfoCard>
							</div>
						</div>
					</section>

					{/* Contact CTA */}
					<section
						id="questions"
						className="relative z-50 scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 text-center md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:text-2xl md:text-3xl">
								Questions?
							</h2>
							<p className="mx-auto mb-6 max-w-2xl text-sm text-muted-foreground">
								Have questions about Nationals competition registration or
								guidelines? Our team is here to help!
							</p>
							<a
								href="/contact"
								className="inline-block border border-primary px-8 py-3 text-sm font-medium uppercase tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
							>
								Contact Us
							</a>
						</div>
					</section>
				</div>

				<aside className="hidden lg:sticky lg:top-32 lg:block lg:self-start">
					<div className="relative">
						<div className="max-h-[calc(100vh-9rem)] overflow-y-auto pr-1">
							<QuickLinksPanel />
						</div>
					</div>
				</aside>
			</div>
		</div>
	)
}
